import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/formatters';

interface StockQuoteCompactProps {
  price: number | null;
  strikePrice?: number;
  optionType?: 'call' | 'put';
  isLoading?: boolean;
  className?: string;
}

/**
 * Compact quote display for chain list - shows price and distance from strike
 */
export function StockQuoteCompact({
  price,
  strikePrice,
  optionType,
  isLoading,
  className,
}: StockQuoteCompactProps) {
  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <span className="text-sm text-gray-400 animate-pulse">Loading...</span>
      </div>
    );
  }

  if (price === null) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <span className="text-sm text-gray-400">--</span>
      </div>
    );
  }

  const distance = strikePrice ? getDistanceFromStrike(price, strikePrice, optionType) : null;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="text-sm font-medium text-gray-900">${price.toFixed(2)}</span>
      {distance && (
        <span
          className={cn(
            'text-xs px-1.5 py-0.5 rounded-full font-medium',
            distance.colorClass
          )}
        >
          {distance.label}
        </span>
      )}
    </div>
  );
}

interface StockQuoteDetailProps {
  price: number | null;
  change: number | null;
  changePercent: number | null;
  strikePrice?: number;
  optionType?: 'call' | 'put';
  isLoading?: boolean;
  className?: string;
}

/**
 * Detailed quote display for chain detail - shows price, daily change, and distance
 */
export function StockQuoteDetail({
  price,
  change,
  changePercent,
  strikePrice,
  optionType,
  isLoading,
  className,
}: StockQuoteDetailProps) {
  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-3', className)}>
        <div className="animate-pulse">
          <div className="h-6 w-20 bg-gray-200 rounded mb-1" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (price === null) {
    return (
      <div className={cn('flex items-center gap-3', className)}>
        <span className="text-gray-400">Quote unavailable</span>
      </div>
    );
  }

  const distance = strikePrice ? getDistanceFromStrike(price, strikePrice, optionType) : null;
  const isPositiveChange = change !== null && change >= 0;

  return (
    <div className={cn('', className)}>
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-semibold text-gray-900">${price.toFixed(2)}</span>
        {change !== null && changePercent !== null && (
          <span
            className={cn(
              'text-sm font-medium',
              isPositiveChange ? 'text-green-600' : 'text-red-600'
            )}
          >
            {isPositiveChange ? '+' : ''}{formatCurrency(change)} ({isPositiveChange ? '+' : ''}{changePercent.toFixed(2)}%)
          </span>
        )}
      </div>
      {distance && (
        <div className="mt-1 flex items-center gap-2">
          <span className="text-sm text-gray-500">Strike distance:</span>
          <span
            className={cn(
              'text-sm px-2 py-0.5 rounded-full font-medium',
              distance.colorClass
            )}
          >
            {distance.label} ({distance.percent.toFixed(1)}%)
          </span>
        </div>
      )}
    </div>
  );
}

interface DistanceInfo {
  percent: number;
  label: string;
  colorClass: string;
  status: 'safe' | 'approaching' | 'danger' | 'itm';
}

function getDistanceFromStrike(
  price: number,
  strike: number,
  optionType?: 'call' | 'put'
): DistanceInfo {
  const diff = price - strike;
  const percentFromStrike = (diff / strike) * 100;

  // For puts: positive diff = OTM (safe), negative = ITM (danger)
  // For calls: negative diff = OTM (safe), positive = ITM (danger)
  const isPut = optionType === 'put';
  const isOTM = isPut ? diff > 0 : diff < 0;
  const absPercent = Math.abs(percentFromStrike);

  let status: DistanceInfo['status'];
  let colorClass: string;

  if (!isOTM) {
    // In the money
    status = 'itm';
    colorClass = 'bg-red-100 text-red-700';
  } else if (absPercent < 2) {
    // Very close to strike
    status = 'danger';
    colorClass = 'bg-red-100 text-red-700';
  } else if (absPercent < 5) {
    // Approaching strike
    status = 'approaching';
    colorClass = 'bg-yellow-100 text-yellow-700';
  } else {
    // Safe distance
    status = 'safe';
    colorClass = 'bg-green-100 text-green-700';
  }

  // Label shows dollar distance (e.g., "+$17.32" or "-$5.00")
  const direction = diff >= 0 ? '+' : '-';
  const label = `${direction}$${Math.abs(diff).toFixed(2)}`;

  return {
    percent: percentFromStrike,
    label,
    colorClass,
    status,
  };
}

export { getDistanceFromStrike };
